import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  FiFolder, FiFileText, FiSearch, FiArrowLeft, FiChevronRight,
  FiHome, FiAlertCircle, FiGrid, FiList,
  FiClock, FiLock, FiLayers, FiArrowUpRight,
  FiCommand, FiChevronDown, FiInbox, FiSlash, FiFolderPlus,
  FiImage, FiUpload, FiX, FiLoader, FiBriefcase, FiUsers, FiTag,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import DocumentTree from './DocumentTree';
import AdvancedSearchPanel from './AdvancedSearchPanel';
import { getLocalized } from '../../i18n/getLocalized';
import { documentLibraryAPI, contractsAPI, companiesAPI, downloadBlob } from '../../services/api';
import { formatDateMedium } from '../../utils/dateFormat';
import { hasPermission } from '../../utils/permissions';
import PageHeader from '../../components/PageHeader/PageHeader';
import './DocumentLibrary.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatDate(iso) {
  return formatDateMedium(iso, '');
}

// Folder entries come in four flavours:
//   • contract      — auto contract folder (keyed by contractId), named after
//                     its contract number per the DMS convention; the contract
//                     name is description-only.
//   • standalone    — user-created library folder (keyed by folderId).
//   • company       — grouping level for the contract's own company (multi-
//                     company tenants); contracts without one fall back to a
//                     tenant-named bucket.
//   • counterparty  — virtual grouping folder named after the counterparty's
//                     system code; the counterparty name is description-only.
const isStandalone = (f) => f?.kind === 'standalone';
const isCompany = (f) => f?.kind === 'company';
const isCounterparty = (f) => f?.kind === 'counterparty';
// counterpartyType — virtual grouping level between company and counterparty,
// named after the counterparty type's system code; the localized type name is
// description-only.
const isCounterpartyType = (f) => f?.kind === 'counterpartyType';
const isGrouping = (f) => isCompany(f) || isCounterpartyType(f) || isCounterparty(f);
// Group keys carry the company so the same counterparty (or type) under two
// companies yields two distinct nodes.
const companyKeyOf = (f) => f?.companyId || '__none';
const folderKeyOf = (f) => {
  if (isCompany(f)) return `co:${f.key}`;
  if (isCounterpartyType(f)) return `cpt:${f.key}`;
  if (isCounterparty(f)) return `cp:${f.key}`;
  return isStandalone(f) ? f.folderId : f.contractId;
};
const folderNameOf = (f) => {
  if (isGrouping(f)) return f.label;
  if (isStandalone(f)) return f.name;
  return f.contractNumber || f.contractName;
};

const SYSTEM_FOLDER_ORDER = ['draft', 'signed', 'supporting'];
// Contract folders fetched per page when a counterparty is open.
const BUCKET_PAGE = 60;

// Every contract is provisioned with Draft / Signed / Supporting Documents.
// Those carry a systemKey so their label follows the UI language; user-created
// folders keep whatever name they were given.
const subfolderName = (folder, t) => (
  folder?.systemKey
    ? t(`documentLibrary.systemFolders.${folder.systemKey}`, { defaultValue: folder.name })
    : folder?.name
);

function useRelativeTime() {
  const { t } = useTranslation();
  return (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t('documentLibrary.time.justNow');
    if (diff < 3600) return t('documentLibrary.time.minutes', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('documentLibrary.time.hours', { n: Math.floor(diff / 3600) });
    if (diff < 604800) return t('documentLibrary.time.days', { n: Math.floor(diff / 86400) });
    if (diff < 2592000) return t('documentLibrary.time.weeks', { n: Math.floor(diff / 604800) });
    return formatDate(iso);
  };
}

const STATUS_TONES = {
  draft:            'neutral',
  in_review:        'info',
  pending_approval: 'warning',
  pending_edit:     'warning',
  approved:         'success',
  fully_executed:   'success',
  active:           'success',
  terminated:       'danger',
  rejected:         'danger',
  archived:         'neutral',
};

function StatusPill({ status }) {
  const { t } = useTranslation();
  const tone = STATUS_TONES[status] || 'neutral';
  const label = status
    ? t(`documentLibrary.status.${status}`, { defaultValue: status.replace(/_/g, ' ') })
    : '—';
  return (
    <span className={`doclib-pill doclib-pill--${tone}`}>
      <span className="doclib-pill__dot" />
      {label}
    </span>
  );
}

// Derive a real file type (PDF / Word / Excel / …) from the document so we can
// show a proper file icon instead of a generic tile. OnlyOffice editor docs are
// Word documents. Only the signed copy is inherently a PDF — a locked draft
// keeps its source type (the uploaded Word document); the lock badge already
// says it is frozen for signing.
function getDocType(doc) {
  if (doc?.category === 'signed') return { key: 'pdf', ext: 'PDF' };
  const m = (doc?.title || '').match(/\.([a-z0-9]+)$/i);
  const e = m ? m[1].toLowerCase() : '';
  if (e === 'pdf') return { key: 'pdf', ext: 'PDF' };
  if (['doc', 'docx', 'rtf', 'odt'].includes(e)) return { key: 'word', ext: e.toUpperCase() };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(e)) return { key: 'excel', ext: e.toUpperCase() };
  if (['ppt', 'pptx'].includes(e)) return { key: 'ppt', ext: e.toUpperCase() };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(e)) return { key: 'image', ext: e.toUpperCase() };
  if (e) return { key: 'generic', ext: e.slice(0, 4).toUpperCase() };
  if (doc?.editorType === 'upload') return { key: 'generic', ext: 'FILE' };
  return { key: 'word', ext: 'DOCX' };
}

function DocFileIcon({ doc, size }) {
  const { key, ext } = getDocType(doc);
  const Icon = key === 'image' ? FiImage : FiFileText;
  return (
    <span className={`doclib-file doclib-file--${key}${size === 'lg' ? ' doclib-file--lg' : ''}`} aria-hidden="true">
      <Icon />
      <span className="doclib-file__tag">{ext}</span>
    </span>
  );
}

// ─── Skeletons ───────────────────────────────────────────────────────────────
function FolderSkeleton() {
  return (
    <div className="doclib-card doclib-card--skeleton" aria-hidden="true">
      <div className="doclib-card__head">
        <div className="shimmer shimmer--icon" />
      </div>
      <div className="shimmer shimmer--line shimmer--w-60" />
      <div className="shimmer shimmer--line shimmer--w-30" />
      <div className="shimmer shimmer--pill" />
    </div>
  );
}

// The grouping levels (company / counterparty) count child folders rather than
// documents, and describe themselves with the mutable display name kept out of
// the folder title per the DMS naming rule.
function folderIconOf(folder) {
  if (isCompany(folder)) return FiBriefcase;
  if (isCounterpartyType(folder)) return FiTag;
  if (isCounterparty(folder)) return FiUsers;
  return FiFolder;
}

function FolderChildCount({ folder }) {
  if (isCompany(folder)) return <><FiFolder /> {folder.counterpartyTypeCount || 0}</>;
  if (isCounterpartyType(folder)) return <><FiFolder /> {folder.counterpartyCount || 0}</>;
  if (isCounterparty(folder)) return <><FiFolder /> {folder.contractCount || 0}</>;
  return <><FiFileText /> {folder.documentCount || 0}</>;
}

function FolderSub({ folder }) {
  const { t } = useTranslation();
  if (isCompany(folder)) {
    return <span className="doclib-card__tag">{t('documentLibrary.companyFolder', { defaultValue: 'Company' })}</span>;
  }
  if (isCounterpartyType(folder)) {
    return folder.description
      ? <span className="doclib-card__desc" title={folder.description}>{folder.description}</span>
      : <span className="doclib-card__tag">{t('documentLibrary.counterpartyTypeFolder', { defaultValue: 'Counterparty Type' })}</span>;
  }
  if (isCounterparty(folder)) {
    return folder.description
      ? <span className="doclib-card__desc" title={folder.description}>{folder.description}</span>
      : <span className="doclib-card__tag">{t('documentLibrary.counterpartyFolder', { defaultValue: 'Counterparty' })}</span>;
  }
  if (isStandalone(folder)) {
    return <span className="doclib-card__tag">{t('documentLibrary.customFolder', { defaultValue: 'Folder' })}</span>;
  }
  return (
    <>
      <span className="doclib-card__desc" title={folder.contractName}>{folder.contractName}</span>
      {folder.versionType === 'addendum' && (
        <span className="badge badge-addendum">
          {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
        </span>
      )}
      <StatusPill status={folder.status} />
    </>
  );
}

// ─── Folder Card (icon view) ─────────────────────────────────────────────────
function FolderCard({ folder, onOpen, index }) {
  const rel = useRelativeTime();
  const Icon = folderIconOf(folder);
  return (
    <button
      className={`doclib-card${isStandalone(folder) ? ' doclib-card--standalone' : ''}${isGrouping(folder) ? ' doclib-card--grouping' : ''}`}
      onClick={() => onOpen(folder)}
      title={folderNameOf(folder)}
      style={{ '--i': index }}
    >
      <div className="doclib-card__head">
        <div className="doclib-card__icon" aria-hidden="true">
          <Icon />
        </div>
        <span className="doclib-card__count">
          <FolderChildCount folder={folder} />
        </span>
        <FiArrowUpRight className="doclib-card__open" aria-hidden="true" />
      </div>
      <div className="doclib-card__title">{folderNameOf(folder)}</div>
      <div className="doclib-card__sub">
        <FolderSub folder={folder} />
      </div>
      <div className="doclib-card__foot">
        <FiClock /> {rel(folder.updatedAt)}
      </div>
    </button>
  );
}

// ─── Folder Row (details view) ───────────────────────────────────────────────
function FolderRow({ folder, onOpen }) {
  const { t } = useTranslation();
  const rel = useRelativeTime();
  const Icon = folderIconOf(folder);
  const description = (isCounterparty(folder) || isCounterpartyType(folder))
    ? folder.description
    : (!isStandalone(folder) && !isCompany(folder) ? folder.contractName : '');
  return (
    <button className="doclib-row" onClick={() => onOpen(folder)} title={folderNameOf(folder)}>
      <div className="doclib-row__main">
        <span className="doclib-row__icon" aria-hidden="true">
          <Icon />
        </span>
        <div className="doclib-row__name-block">
          <div className="doclib-row__title">{folderNameOf(folder)}</div>
          {description && (
            <div className="doclib-row__code">{description}</div>
          )}
        </div>
      </div>
      <div className="doclib-row__cell doclib-row__cell--status">
        {isCompany(folder)
          ? <span className="doclib-card__tag">{t('documentLibrary.companyFolder', { defaultValue: 'Company' })}</span>
          : isCounterpartyType(folder)
            ? <span className="doclib-card__tag">{t('documentLibrary.counterpartyTypeFolder', { defaultValue: 'Counterparty Type' })}</span>
            : isCounterparty(folder)
              ? <span className="doclib-card__tag">{t('documentLibrary.counterpartyFolder', { defaultValue: 'Counterparty' })}</span>
              : isStandalone(folder)
                ? <span className="doclib-card__tag">{t('documentLibrary.customFolder', { defaultValue: 'Folder' })}</span>
                : <StatusPill status={folder.status} />}
      </div>
      <div className="doclib-row__cell doclib-row__cell--count">
        <span className="doclib-row__dim"><FolderChildCount folder={folder} /></span>
      </div>
      <div className="doclib-row__cell doclib-row__cell--date">
        <span className="doclib-row__dim">{rel(folder.updatedAt)}</span>
      </div>
      <FiChevronRight className="doclib-row__chev" />
    </button>
  );
}

// ─── Folder listing (grid / details) ─────────────────────────────────────────
function FolderGrid({ folders, onOpen, search, viewMode, loading, footer = null }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={viewMode === 'list' ? 'doclib-list' : 'doclib-grid'}>
        {Array.from({ length: 8 }).map((_, i) => <FolderSkeleton key={i} />)}
      </div>
    );
  }

  if (folders.length === 0) {
    return (
      <div className="doclib-empty">
        <div className="doclib-empty__art" aria-hidden="true">
          <FiInbox />
        </div>
        <h3>{t('documentLibrary.noFoldersFound')}</h3>
        <p>{search ? t('documentLibrary.tryDifferentSearch') : t('documentLibrary.noDocumentsUploaded')}</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <>
        <div className="doclib-list">
        <div className="doclib-list__head" role="presentation">
          <span>{t('documentLibrary.colName')}</span>
          <span>{t('documentLibrary.colStatus')}</span>
          <span>{t('documentLibrary.colDocs')}</span>
          <span>{t('documentLibrary.colUpdated')}</span>
          <span />
        </div>
        {folders.map((f) => <FolderRow key={folderKeyOf(f)} folder={f} onOpen={onOpen} />)}
        </div>
        {footer}
      </>
    );
  }

  return (
    <>
      <div className="doclib-grid">
        {folders.map((f, i) => <FolderCard key={folderKeyOf(f)} folder={f} onOpen={onOpen} index={i} />)}
      </div>
      {footer}
    </>
  );
}

// ─── Document Tile (file, icon-view) ─────────────────────────────────────────
function DocumentTile({ doc, folder, index }) {
  const rel = useRelativeTime();
  return (
    <Link
      to={`/contracts/${folder.contractId}/documents/${doc._id}/editor`}
      state={{ from: 'library', folderId: doc.folderId || null }}
      className="doclib-tile"
      style={{ '--i': index }}
      title={doc.title}
    >
      <span className="doclib-tile__art">
        <DocFileIcon doc={doc} size="lg" />
        {doc.isLocked && (
          <span className="doclib-tile__lock" aria-hidden="true"><FiLock /></span>
        )}
      </span>
      <span className="doclib-tile__name">{doc.title}</span>
      <span className="doclib-tile__meta">v{doc.currentVersion} · {rel(doc.updatedAt)}</span>
    </Link>
  );
}

// ─── File Tile (standalone library file — downloads/previews) ────────────────
function FileTile({ file, onOpen, index, busy }) {
  const rel = useRelativeTime();
  return (
    <button
      type="button"
      className="doclib-tile"
      style={{ '--i': index }}
      title={file.name}
      onClick={() => onOpen(file)}
      disabled={busy}
    >
      <span className="doclib-tile__art">
        <DocFileIcon doc={{ title: file.name }} size="lg" />
      </span>
      <span className="doclib-tile__name">{file.name}</span>
      <span className="doclib-tile__meta">{rel(file.updatedAt || file.createdAt)}</span>
    </button>
  );
}

// ─── Sub-folder tile (inside a contract folder) ──────────────────────────────
function SubfolderTile({ folder, docCount, onOpen, index }) {
  const { t } = useTranslation();
  const name = subfolderName(folder, t);
  return (
    <button
      type="button"
      className={`doclib-tile doclib-tile--folder${folder.systemKey ? ' doclib-tile--system' : ''}`}
      style={{ '--i': index }}
      title={name}
      onClick={() => onOpen(folder)}
    >
      <span className="doclib-tile__art">
        <span className="doclib-tile__folder-icon" aria-hidden="true"><FiFolder /></span>
      </span>
      <span className="doclib-tile__name">{name}</span>
      <span className="doclib-tile__meta">{docCount}</span>
    </button>
  );
}

// ─── Folder Contents (icon grid, Explorer-style) ─────────────────────────────
function FolderContents({ folder, childFolders, folderCounts, documents, search, loading, onOpenSub, onOpenFile, onOpenAttachment, busyFileId }) {
  const { t } = useTranslation();
  const standalone = isStandalone(folder);

  if (loading) {
    return (
      <div className="doclib-files">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="doclib-tile doclib-tile--skeleton" aria-hidden="true">
            <div className="shimmer shimmer--file" />
            <div className="shimmer shimmer--line shimmer--w-60" />
            <div className="shimmer shimmer--line shimmer--w-30" />
          </div>
        ))}
      </div>
    );
  }

  if (documents.length === 0 && childFolders.length === 0) {
    return (
      <div className="doclib-empty">
        <div className="doclib-empty__art" aria-hidden="true">
          <FiFileText />
        </div>
        <h3>{t('documentLibrary.noDocuments')}</h3>
        <p>{search ? t('documentLibrary.noDocumentsMatch') : t('documentLibrary.noDocumentsInFolder')}</p>
      </div>
    );
  }

  return (
    <div className="doclib-files">
      {childFolders.map((f, i) => (
        <SubfolderTile
          key={f._id}
          folder={f}
          docCount={t('documentLibrary.itemCount', { n: folderCounts[f._id] || 0, defaultValue: '{{n}} items' })}
          onOpen={onOpenSub}
          index={i}
        />
      ))}
      {documents.map((d, i) => (
        standalone
          ? <FileTile key={d._id} file={d} onOpen={onOpenFile} index={childFolders.length + i} busy={busyFileId === d._id} />
          : d.kind === 'attachment'
            // Plain contract attachments (Supporting Documents uploads) —
            // read-only entries that download instead of opening the editor.
            ? <FileTile key={d._id} file={d} onOpen={onOpenAttachment} index={childFolders.length + i} busy={busyFileId === d._id} />
            : <DocumentTile key={d._id} doc={d} folder={folder} index={childFolders.length + i} />
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
function hexToRgb(hex) {
  if (!hex) return '99, 102, 241';
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export default function DocumentLibrary() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  // "Back to documents" from the editor asks us to reopen the folder the
  // document lives in ({ contractId, folderId }); consumed once after load.
  const navigate = useNavigate();
  const reopenRef = useRef(location.state?.reopen || null);
  const accent = useSelector(
    (state) => state.settings?.branding?.accentColor
      || state.settings?.config?.branding?.accentColor
      || '#6366f1'
  );
  const accentRgb = useMemo(() => hexToRgb(accent), [accent]);
  // The root listing: the hierarchy's groups (already counted and keyed by
  // the server), the standalone folders and the totals. Contract folders are
  // NOT in it — they are paged per counterparty (see `bucket` below), so a
  // tenant with 20k contracts costs the browser one small response, not 20k
  // cards and 20k tree rows rendered at once.
  const [groups, setGroups] = useState({ companies: [], counterpartyTypes: [], counterparties: [] });
  const [standaloneList, setStandaloneList] = useState([]);
  const [totals, setTotals] = useState({ folders: 0, documents: 0 });
  const [company, setCompany] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(true);
  // Contract folders of the counterparty open in the main pane, one page at
  // a time.
  const [bucket, setBucket] = useState({ key: null, items: [], total: 0, page: 0, loading: false });
  // DMS drill-down state: root → company (the contract's company) →
  // counterparty type → counterparty → contract.
  const [openCompany, setOpenCompany] = useState(null);
  const [openCounterpartyType, setOpenCounterpartyType] = useState(null);
  const [openCounterparty, setOpenCounterparty] = useState(null);
  const [openFolder, setOpenFolder] = useState(null);
  const [folderDocs, setFolderDocs] = useState([]);
  const [subFolders, setSubFolders] = useState([]);
  // Stack of opened sub-folders inside the contract folder ([] = its root).
  const [path, setPath] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reloadFoldersKey, setReloadFoldersKey] = useState(0);
  const [busyFileId, setBusyFileId] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  // Company filter (multi-company tenants) — narrows the contract folders to
  // one company on top of the backend access rules.
  const [companyFilter, setCompanyFilter] = useState('');
  const [companiesList, setCompaniesList] = useState([]);
  // The library always opens in grid view; the list toggle lasts for the session only.
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('doclib.sort') || 'updated');
  const [sortOpen, setSortOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const searchRef = useRef(null);
  const sortRef = useRef(null);
  const fileInputRef = useRef(null);
  // A tree click can target a sub-folder directly. Opening its space resets
  // `path`, so the intended trail is parked here and applied by that effect.
  const pendingPathRef = useRef(null);

  // Folder creation and document upload are independent permissions (CLM-62):
  // a user may hold either one without the other.
  const canCreateFolder = hasPermission(user?.role, 'create_folders', user?.effectivePermissions || null);
  const canUpload = hasPermission(user?.role, 'upload_documents', user?.effectivePermissions || null);

  // Persist sort
  useEffect(() => { localStorage.setItem('doclib.sort', sortBy); }, [sortBy]);

  // Company filter options
  useEffect(() => {
    companiesAPI.list().then(r => setCompaniesList(r.data.companies || [])).catch(() => {});
  }, []);

  // Load the root listing: groups with counts, standalone folders, totals.
  useEffect(() => {
    let cancelled = false;
    setLoadingFolders(true);
    setError(null);
    documentLibraryAPI.listFolders(companyFilter ? { companyId: companyFilter } : undefined)
      .then((res) => {
        if (cancelled) return;
        setGroups(res.data.groups || { companies: [], counterpartyTypes: [], counterparties: [] });
        setStandaloneList([...(res.data.standalone || [])]
          .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setTotals(res.data.totals || { folders: 0, documents: 0 });
        setCompany(res.data.company || null);
        // Returning from the editor: jump straight back into the folder
        // the opened document belongs to. Its card is fetched on its own —
        // contract folders no longer arrive with the root listing.
        const reopen = reopenRef.current;
        if (reopen && reopen.contractId) {
          documentLibraryAPI.getContractCard(reopen.contractId)
            .then((r) => {
              if (cancelled) return;
              const card = r.data.folders && r.data.folders[0];
              if (card) setOpenFolder(card);
              else reopenRef.current = null;
            })
            .catch(() => { reopenRef.current = null; });
        }
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || t('documentLibrary.failedToLoad')); })
      .finally(() => !cancelled && setLoadingFolders(false));
    return () => { cancelled = true; };
  }, [t, reloadFoldersKey, companyFilter]);

  // Load contents on folder open (and after create/upload). Contract folders
  // return { folders, documents }; standalone folders return { folders, files }.
  useEffect(() => {
    if (!openFolder) return;
    let cancelled = false;
    setLoadingDocs(true);
    const request = isStandalone(openFolder)
      ? documentLibraryAPI.getStandaloneFolder(openFolder.folderId)
      : documentLibraryAPI.getFolder(openFolder.contractId);
    request
      .then((res) => {
        if (cancelled) return;
        const tree = res.data.folders || [];
        setFolderDocs(res.data.documents || res.data.files || []);
        setSubFolders(tree);
        // Finish an editor-return reopen (contract folders only): rebuild the
        // breadcrumb path down to the folder the document lives in.
        const reopen = reopenRef.current;
        const matchesContract = reopen && !isStandalone(openFolder)
          && reopen.contractId === openFolder.contractId;
        // Search hits into nested standalone sub-folders reuse the same
        // mechanism: open the root card, then walk down to the file's folder.
        const matchesStandalone = reopen && isStandalone(openFolder)
          && reopen.standaloneFolderId === openFolder.folderId;
        if (matchesContract || matchesStandalone) {
          if (reopen.folderId) {
            const byId = new Map(tree.map((f) => [String(f._id), f]));
            const chain = [];
            let cur = byId.get(String(reopen.folderId));
            while (cur) {
              // The standalone response includes the root folder itself —
              // the breadcrumb path only holds folders below it.
              if (matchesStandalone && String(cur._id) === String(openFolder.folderId)) break;
              chain.unshift(cur);
              cur = cur.parentId ? byId.get(String(cur.parentId)) : null;
            }
            if (chain.length) setPath(chain);
          }
          reopenRef.current = null;
        }
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || t('documentLibrary.failedToLoad')); })
      .finally(() => !cancelled && setLoadingDocs(false));
    return () => { cancelled = true; };
  }, [openFolder, refreshKey, t]);

  // Entering/leaving a contract folder resets the sub-path and search — unless
  // a tree click asked for a specific trail inside the space being opened.
  useEffect(() => {
    setPath(pendingPathRef.current || []);
    pendingPathRef.current = null;
    setSearch('');
  }, [openFolder]);

  // Keyboard shortcut: "/" to focus search
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const isTyping = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && el === searchRef.current) {
        setSearch('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close sort dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    if (sortOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [sortOpen]);

  // Derived: the grouping levels arrive from the server already counted and
  // keyed (company → counterparty type → counterparty). Only what needs the
  // viewer's language is resolved here — the captions of the catch-all
  // buckets and the localized type names.
  const companyGroups = useMemo(() => groups.companies.map((g) => ({
    ...g,
    label: g.label || company?.name || t('documentLibrary.companyFolder', { defaultValue: 'Company' }),
  })), [groups.companies, company, t]);

  const counterpartyTypeGroups = useMemo(() => groups.counterpartyTypes.map((g) => ({
    ...g,
    label: g.label || t('documentLibrary.noCounterpartyType', { defaultValue: 'No Counterparty Type' }),
    description: g.code ? (getLocalized(g.description, i18n.language) || '') : '',
  })), [groups.counterpartyTypes, t, i18n.language]);

  const counterpartyGroups = useMemo(() => groups.counterparties.map((g) => ({
    ...g,
    label: g.label || t('documentLibrary.noCounterparty', { defaultValue: 'No Counterparty' }),
  })), [groups.counterparties, t]);

  // Contract folders of the open counterparty, a page at a time. The search
  // box filters them on the server — the bucket may hold thousands — while
  // the grouping levels above are small and filter in place below.
  const bucketKey = openCounterparty ? openCounterparty.key : null;
  const bucketSort = sortBy === 'name' ? 'name' : 'updated';
  const bucketParams = useCallback((page) => ({
    companyKey: openCounterparty.companyKey,
    counterpartyCode: openCounterparty.code || '',
    page,
    limit: BUCKET_PAGE,
    search: search.trim() || undefined,
    sort: bucketSort,
    ...(companyFilter ? { companyId: companyFilter } : {}),
  }), [openCounterparty, search, bucketSort, companyFilter]);

  useEffect(() => {
    if (!bucketKey || openFolder) return undefined;
    let cancelled = false;
    // Typing is debounced; navigation is not.
    const timer = setTimeout(() => {
      setBucket((b) => ({
        key: bucketKey,
        items: b.key === bucketKey ? b.items : [],
        total: b.key === bucketKey ? b.total : 0,
        page: 0,
        loading: true,
      }));
      documentLibraryAPI.listContractFolders(bucketParams(1))
        .then((res) => {
          if (cancelled) return;
          setBucket({
            key: bucketKey,
            items: res.data.folders || [],
            total: res.data.pagination ? res.data.pagination.total : 0,
            page: 1,
            loading: false,
          });
        })
        .catch((err) => {
          if (cancelled) return;
          setBucket({ key: bucketKey, items: [], total: 0, page: 0, loading: false });
          setError(err.response?.data?.error || t('documentLibrary.failedToLoad'));
        });
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bucketKey, openFolder, search, bucketParams, reloadFoldersKey, t]);

  const loadMoreBucket = useCallback(() => {
    if (!openCounterparty || bucket.loading || bucket.items.length >= bucket.total) return;
    const key = openCounterparty.key;
    const nextPage = bucket.page + 1;
    setBucket((b) => ({ ...b, loading: true }));
    documentLibraryAPI.listContractFolders(bucketParams(nextPage))
      .then((res) => setBucket((b) => {
        if (b.key !== key) return b;
        const seen = new Set(b.items.map((f) => f.contractId));
        const fresh = (res.data.folders || []).filter((f) => !seen.has(f.contractId));
        return {
          ...b,
          items: [...b.items, ...fresh],
          total: res.data.pagination ? res.data.pagination.total : b.total,
          page: nextPage,
          loading: false,
        };
      }))
      .catch(() => setBucket((b) => ({ ...b, loading: false })));
  }, [openCounterparty, bucket, bucketParams]);

  // Folder list of the current drill-down level, filtered + sorted.
  const visibleFolders = useMemo(() => {
    const sorters = {
      updated: (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
      name:    (a, b) => (folderNameOf(a) || '').localeCompare(folderNameOf(b) || ''),
      count:   (a, b) => (b.documentCount || 0) - (a.documentCount || 0),
    };

    if (openCounterparty) {
      // Already filtered and ordered by the server — except by document
      // count, which needs the counts only the loaded rows carry.
      const list = [...bucket.items];
      if (sortBy === 'count') list.sort(sorters.count);
      return list;
    }

    let list;
    if (openCounterpartyType) {
      list = counterpartyGroups.filter((g) => g.counterpartyTypeKey === openCounterpartyType.key);
    } else if (openCompany) {
      list = counterpartyTypeGroups.filter((g) => g.companyKey === openCompany.key);
    } else {
      list = [...companyGroups, ...standaloneList];
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((f) =>
        folderNameOf(f)?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q));
    } else {
      list = [...list];
    }
    list.sort(sorters[sortBy] || sorters.updated);
    return list;
  }, [openCompany, openCounterpartyType, openCounterparty, bucket.items, counterpartyTypeGroups, counterpartyGroups, companyGroups, standaloneList, search, sortBy]);

  // Root level inside the opened folder: contract folders start at null (the
  // contract root); standalone folders start at the folder itself.
  const rootId = openFolder && isStandalone(openFolder) ? openFolder.folderId : null;
  // Current level within the opened folder's sub-tree.
  const currentSubId = path.length ? path[path.length - 1]._id : rootId;

  const childFolders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subFolders
      .filter((f) => String(f.parentId || '') === String(currentSubId || ''))
      .filter((f) => !q || subfolderName(f, t)?.toLowerCase().includes(q))
      // Standard folders first, in Draft → Signed → Supporting order.
      .sort((a, b) => {
        const rank = (f) => (f.systemKey ? SYSTEM_FOLDER_ORDER.indexOf(f.systemKey) : SYSTEM_FOLDER_ORDER.length);
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
        return subfolderName(a, t).localeCompare(subfolderName(b, t));
      });
  }, [subFolders, currentSubId, search, t]);

  // Direct items (sub-folders + documents) per folder, for the tile badge.
  const folderCounts = useMemo(() => {
    const counts = {};
    for (const f of subFolders) {
      if (f.parentId) counts[f.parentId] = (counts[f.parentId] || 0) + 1;
    }
    for (const d of folderDocs) {
      if (d.folderId) counts[d.folderId] = (counts[d.folderId] || 0) + 1;
    }
    return counts;
  }, [subFolders, folderDocs]);

  const visibleDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return folderDocs
      .filter((d) => String(d.folderId || '') === String(currentSubId || ''))
      .filter((d) => !q || d.title?.toLowerCase().includes(q));
  }, [folderDocs, currentSubId, search]);

  const onClearSearch = useCallback(() => {
    setSearch('');
    searchRef.current?.focus();
  }, []);

  const openSubFolder = useCallback((f) => {
    setPath((p) => [...p, f]);
    setSearch('');
  }, []);

  // ── DMS level navigation ──
  const goRoot = useCallback(() => {
    setOpenFolder(null);
    setOpenCounterparty(null);
    setOpenCounterpartyType(null);
    setOpenCompany(null);
    setSearch('');
  }, []);

  const goCompany = useCallback((group) => {
    setOpenFolder(null);
    setOpenCounterparty(null);
    setOpenCounterpartyType(null);
    setOpenCompany(group);
    setSearch('');
  }, []);

  const goCounterpartyType = useCallback((group) => {
    setOpenFolder(null);
    setOpenCounterparty(null);
    // Line the company level up behind the type so breadcrumb and back
    // navigation stay correct however this level was reached.
    setOpenCompany(companyGroups.find((c) => c.key === group.companyKey) || null);
    setOpenCounterpartyType(group);
    setSearch('');
  }, [companyGroups]);

  const goCounterparty = useCallback((group) => {
    setOpenFolder(null);
    setOpenCompany(companyGroups.find((c) => c.key === group.companyKey) || null);
    setOpenCounterpartyType(
      counterpartyTypeGroups.find((g) => g.key === group.counterpartyTypeKey) || null
    );
    setOpenCounterparty(group);
    setSearch('');
  }, [companyGroups, counterpartyTypeGroups]);

  // Tree: open a sub-folder directly. If its space is already open we can set
  // the trail now; otherwise park it for the open-folder effect to apply.
  const openTreeSubFolder = useCallback((root, trail) => {
    setSearch('');
    setOpenFolder((cur) => {
      if (cur && folderKeyOf(cur) === folderKeyOf(root)) {
        setPath(trail);
        return cur;
      }
      pendingPathRef.current = trail;
      return root;
    });
  }, []);

  // Tree: open a document. Contract documents go to the editor (same target as
  // the tiles in the main pane); standalone library files preview in place.
  const openTreeFile = (doc, root, trail) => {
    if (isStandalone(root)) { handleOpenFile(doc); return; }
    // Attachments download — they have no editor document behind them.
    if (doc.kind === 'attachment') { openAttachment(root.contractId, doc); return; }
    navigate(`/contracts/${root.contractId}/documents/${doc._id}/editor`, {
      state: { from: 'library', folderId: trail?.[trail.length - 1]?._id || doc.folderId || null },
    });
  };

  // Where the main pane currently is, as a chain of tree node keys. The tree
  // both highlights the last entry and expands everything along the way, so
  // drilling down with the cards keeps the sidebar in step.
  const treePath = useMemo(() => {
    if (openFolder && isStandalone(openFolder)) {
      const keys = [`sa:${openFolder.folderId}`];
      path.forEach((p) => keys.push(`f:${openFolder.folderId}:${p._id}`));
      return keys;
    }
    if (openFolder) {
      const ck = companyKeyOf(openFolder);
      const keys = [
        `co:${ck}`,
        `cpt:${ck}|${openFolder.counterpartyTypeCode || '__none'}`,
        `cp:${ck}|${openFolder.counterpartyCode || '__none'}`,
        `ct:${openFolder.contractId}`,
      ];
      path.forEach((p) => keys.push(`f:${openFolder.contractId}:${p._id}`));
      return keys;
    }
    if (openCounterparty) {
      return [
        `co:${openCounterparty.companyKey}`,
        `cpt:${openCounterparty.counterpartyTypeKey}`,
        `cp:${openCounterparty.key}`,
      ];
    }
    if (openCounterpartyType) {
      return [`co:${openCounterpartyType.companyKey}`, `cpt:${openCounterpartyType.key}`];
    }
    if (openCompany) return [`co:${openCompany.key}`];
    return [];
  }, [openFolder, path, openCounterparty, openCounterpartyType, openCompany]);

  const activeTreeKey = treePath.length ? treePath[treePath.length - 1] : '__root';

  // One entry point for every folder kind in cards, rows and the tree.
  const openEntry = useCallback((f) => {
    if (isCompany(f)) goCompany(f);
    else if (isCounterpartyType(f)) goCounterpartyType(f);
    else if (isCounterparty(f)) goCounterparty(f);
    else setOpenFolder(f);
  }, [goCompany, goCounterpartyType, goCounterparty]);

  // An advanced-search hit navigates to what it matched: document hits open
  // the owning contract's folder in the library (standalone files their
  // folder, descending into the sub-folder the file lives in), contract
  // metadata hits go to the contract detail page. Superseded contract versions
  // have no folder card, so they fall back to the detail page too.
  const openSearchResult = useCallback((r) => {
    setSearch('');
    if (r.kind === 'libraryFile') {
      // The card may not be loaded (created since the list fetch) — a
      // synthetic standalone entry is enough for the folder-content effect.
      const target = standaloneList.find((f) => f.folderId === r.folderId)
        || { kind: 'standalone', folderId: r.folderId, name: r.folderName || '' };
      if (r.directFolderId && r.directFolderId !== r.folderId) {
        reopenRef.current = { standaloneFolderId: r.folderId, folderId: r.directFolderId };
      }
      setOpenFolder(target);
      return;
    }
    if (r.kind === 'document' && r.contractId) {
      // The contract's folder card is not held client-side any more (folders
      // are paged per counterparty) — fetch it, then open it.
      documentLibraryAPI.getContractCard(r.contractId)
        .then((res) => {
          const card = res.data.folders && res.data.folders[0];
          if (card) setOpenFolder(card);
          else navigate(`/contracts/${r.contractId}`);
        })
        .catch(() => navigate(`/contracts/${r.contractId}`));
      return;
    }
    if (r.contractId) navigate(`/contracts/${r.contractId}`);
  }, [standaloneList, navigate]);

  // A contract folder can be opened without walking the hierarchy (tree click,
  // editor return) — line the company/type/counterparty context up behind it
  // so the breadcrumb and back navigation stay correct.
  useEffect(() => {
    if (!openFolder || isStandalone(openFolder)) return;
    const ck = companyKeyOf(openFolder);
    setOpenCompany((cur) => {
      if (cur && cur.key === ck) return cur;
      return companyGroups.find((c) => c.key === ck) || null;
    });
    const typeKey = `${ck}|${openFolder.counterpartyTypeCode || '__none'}`;
    setOpenCounterpartyType((cur) => {
      if (cur && cur.key === typeKey) return cur;
      return counterpartyTypeGroups.find((g) => g.key === typeKey) || null;
    });
    const cpKey = `${ck}|${openFolder.counterpartyCode || '__none'}`;
    setOpenCounterparty((cur) => {
      if (cur && cur.key === cpKey) return cur;
      return counterpartyGroups.find((g) => g.key === cpKey) || null;
    });
  }, [openFolder, companyGroups, counterpartyTypeGroups, counterpartyGroups]);

  // Standalone folders live at the root, outside the company hierarchy.
  useEffect(() => {
    if (openFolder && isStandalone(openFolder)) {
      setOpenCompany(null);
      setOpenCounterpartyType(null);
      setOpenCounterparty(null);
    }
  }, [openFolder]);

  // Back: sub-folder → contract → counterparty → type → company → root.
  const goBack = useCallback(() => {
    setSearch('');
    if (path.length > 0) setPath((p) => p.slice(0, -1));
    else if (openFolder) setOpenFolder(null);
    else if (openCounterparty) setOpenCounterparty(null);
    else if (openCounterpartyType) setOpenCounterpartyType(null);
    else setOpenCompany(null);
  }, [path.length, openFolder, openCounterparty, openCounterpartyType]);

  // Create a folder in the current context:
  //  • no folder open       → a new top-level standalone folder
  //  • inside a standalone   → a nested standalone folder
  //  • inside a contract     → a contract sub-folder
  const handleCreateFolder = async (e) => {
    e?.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      if (!openFolder) {
        await documentLibraryAPI.createStandaloneFolder({ name, parentId: null });
      } else if (isStandalone(openFolder)) {
        await documentLibraryAPI.createStandaloneFolder({ name, parentId: currentSubId });
      } else {
        await documentLibraryAPI.createFolder(openFolder.contractId, { name, parentId: currentSubId });
      }
      toast.success(t('documentLibrary.folderCreated', { defaultValue: 'Folder created' }));
      setNewFolderOpen(false);
      setNewFolderName('');
      // Top-level create refreshes the folder list; in-folder create refreshes contents.
      if (!openFolder) setReloadFoldersKey((k) => k + 1);
      else setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.error || t('documentLibrary.folderCreateFailed', { defaultValue: 'Could not create folder' }));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleUploadFiles = async (files) => {
    if (!openFolder || !files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (isStandalone(openFolder)) {
          await documentLibraryAPI.uploadStandaloneFile(currentSubId, file);
        } else {
          await documentLibraryAPI.uploadFile(openFolder.contractId, file, currentSubId);
        }
      }
      toast.success(t('documentLibrary.uploadSuccess', { n: files.length, defaultValue: 'Upload complete' }));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.error || t('documentLibrary.uploadFailed', { defaultValue: 'Upload failed' }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // L: only a PDF is opened inline (typed blob → the browser's own viewer).
  // Anything else — HTML, SVG, Office files… — is handed to the download
  // manager instead of being rendered in a same-origin blob: tab, where an
  // uploaded HTML/SVG file would execute script with the app's origin.
  const isPdf = (blob, name) =>
    blob?.type === 'application/pdf' || /\.pdf$/i.test(name || '');
  const openOrDownloadBlob = (blob, name) => {
    if (isPdf(blob, name)) {
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const win = window.open(url, '_blank', 'noopener');
      // Popup blocked → fall back to a direct download.
      if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // Standalone files download/preview through an authenticated blob request
  // (so the JWT rides in the header), then open in a new tab (PDF) or download.
  const handleOpenFile = async (file) => {
    setBusyFileId(file._id);
    try {
      const res = await documentLibraryAPI.downloadStandaloneFile(file._id);
      openOrDownloadBlob(res.data, file.name);
    } catch (err) {
      toast.error(err.response?.data?.error || t('documentLibrary.downloadFailed', { defaultValue: 'Could not open file' }));
    } finally {
      setBusyFileId(null);
    }
  };

  // Contract attachments surfaced in the Supporting Documents folder are
  // plain files on the contract — open/download them via the attachments
  // endpoint rather than the document editor.
  const openAttachment = async (contractId, att) => {
    if (!contractId) return;
    setBusyFileId(att._id);
    try {
      const url = contractsAPI.downloadUrl(contractId, att._id);
      // L: shared axios instance — honours the impersonation/delegation token
      // store and the refresh interceptor, unlike a raw fetch + localStorage.
      const res = await downloadBlob(url);
      openOrDownloadBlob(res.data, att.name || att.title || 'attachment');
    } catch {
      toast.error(t('documentLibrary.downloadFailed', { defaultValue: 'Could not open file' }));
    } finally {
      setBusyFileId(null);
    }
  };

  const handleOpenAttachment = (att) => openAttachment(openFolder?.contractId, att);

  const sortOptions = [
    { id: 'updated', label: t('documentLibrary.sortUpdated') },
    { id: 'name',    label: t('documentLibrary.sortName') },
    { id: 'count',   label: t('documentLibrary.sortCount') },
  ];
  const currentSort = sortOptions.find(o => o.id === sortBy) || sortOptions[0];

  return (
    <div className="doclib" style={{ '--doclib-accent': accent, '--doclib-accent-rgb': accentRgb }}>
      {/* Compact page header */}
      <header className="doclib-head">
        {/* Titled after the sidebar entry that leads here — "Dokümanlar". */}
        <PageHeader
          title={t('documentLibrary.title')}
          description={t('pageDescriptions.documents')}
        />
        <div className="doclib-head__stats">
          <span className="doclib-head__stat">
            <FiLayers /> <b>{totals.folders}</b> {t('documentLibrary.folders')}
          </span>
          <span className="doclib-head__stat">
            <FiFileText /> <b>{totals.documents}</b> {t('documentLibrary.documentsLabel')}
          </span>
        </div>
      </header>

      {error && (
        <div className="doclib-alert" role="alert">
          <FiAlertCircle /> {error}
        </div>
      )}

      {/* Explorer window */}
      <div className="doclib-explorer">
        {/* ── Left tree: Locations ── */}
        <aside className="doclib-tree">
          <div className="doclib-tree__head">{t('documentLibrary.locations', { defaultValue: 'Locations' })}</div>
          <DocumentTree
            companyGroups={companyGroups}
            counterpartyTypeGroups={counterpartyTypeGroups}
            counterpartyGroups={counterpartyGroups}
            standaloneList={standaloneList}
            loading={loadingFolders}
            pinnedContract={openFolder && !isStandalone(openFolder) ? openFolder : null}
            activeKey={activeTreeKey}
            openPath={treePath}
            onOpenRoot={goRoot}
            onOpenCompany={goCompany}
            onOpenCounterpartyType={goCounterpartyType}
            onOpenCounterparty={goCounterparty}
            onOpenContract={setOpenFolder}
            onOpenSubFolder={openTreeSubFolder}
            onOpenFile={openTreeFile}
            subfolderName={subfolderName}
          />
        </aside>

        {/* ── Main pane ── */}
        <section className="doclib-main">
          {/* Path bar */}
          <div className="doclib-pathbar">
            {(openFolder || openCompany) && (
              <button className="doclib-back" onClick={goBack} title={t('documentLibrary.back')}>
                <FiArrowLeft />
              </button>
            )}
            <nav className="doclib-crumbs" aria-label="Breadcrumb">
              <button onClick={goRoot} className="doclib-crumbs__link">
                <FiHome /> {t('documentLibrary.allFolders', { defaultValue: 'All folders' })}
              </button>
              {openCompany && (
                <>
                  <FiChevronRight />
                  {!openCounterpartyType && !openCounterparty && !openFolder ? (
                    <span className="doclib-crumbs__current" title={openCompany.label}>
                      <FiBriefcase /> {openCompany.label}
                    </span>
                  ) : (
                    <button className="doclib-crumbs__link" onClick={() => goCompany(openCompany)} title={openCompany.label}>
                      <FiBriefcase /> {openCompany.label}
                    </button>
                  )}
                </>
              )}
              {openCounterpartyType && (
                <>
                  <FiChevronRight />
                  {!openCounterparty && !openFolder ? (
                    <span className="doclib-crumbs__current" title={openCounterpartyType.description || openCounterpartyType.label}>
                      <FiTag /> {openCounterpartyType.label}
                    </span>
                  ) : (
                    <button
                      className="doclib-crumbs__link"
                      onClick={() => goCounterpartyType(openCounterpartyType)}
                      title={openCounterpartyType.description || openCounterpartyType.label}
                    >
                      <FiTag /> {openCounterpartyType.label}
                    </button>
                  )}
                </>
              )}
              {openCounterparty && (
                <>
                  <FiChevronRight />
                  {!openFolder ? (
                    <span className="doclib-crumbs__current" title={openCounterparty.description || openCounterparty.label}>
                      <FiUsers /> {openCounterparty.label}
                    </span>
                  ) : (
                    <button
                      className="doclib-crumbs__link"
                      onClick={() => goCounterparty(openCounterparty)}
                      title={openCounterparty.description || openCounterparty.label}
                    >
                      <FiUsers /> {openCounterparty.label}
                    </button>
                  )}
                </>
              )}
              {openFolder && (
                <>
                  <FiChevronRight />
                  {path.length === 0 ? (
                    <span className="doclib-crumbs__current" title={folderNameOf(openFolder)}>
                      <FiFolder /> {folderNameOf(openFolder)}
                    </span>
                  ) : (
                    <button className="doclib-crumbs__link" onClick={() => setPath([])} title={folderNameOf(openFolder)}>
                      <FiFolder /> {folderNameOf(openFolder)}
                    </button>
                  )}
                  {path.map((f, i) => (
                    <React.Fragment key={f._id}>
                      <FiChevronRight />
                      {i === path.length - 1 ? (
                        <span className="doclib-crumbs__current" title={subfolderName(f, t)}>
                          <FiFolder /> {subfolderName(f, t)}
                        </span>
                      ) : (
                        <button className="doclib-crumbs__link" onClick={() => setPath(path.slice(0, i + 1))} title={subfolderName(f, t)}>
                          <FiFolder /> {subfolderName(f, t)}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </nav>
            {openFolder && !isStandalone(openFolder) && <StatusPill status={openFolder.status} />}
          </div>

          {/* Toolbar */}
          <div className="doclib-toolbar">
            <div className="doclib-search">
              <FiSearch className="doclib-search__icon" />
              <input
                ref={searchRef}
                type="text"
                placeholder={openFolder ? t('documentLibrary.searchDocuments') : t('documentLibrary.searchFolders')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('documentLibrary.searchAria')}
              />
              {search ? (
                <button className="doclib-search__clear" onClick={onClearSearch} aria-label={t('documentLibrary.clearSearch')}>
                  <FiSlash />
                </button>
              ) : (
                <kbd className="doclib-search__kbd"><FiCommand />/</kbd>
              )}
            </div>

            {openFolder && (canCreateFolder || canUpload) && (
              <div className="doclib-actions">
                {canCreateFolder && (
                  <button
                    type="button"
                    className="doclib-btn"
                    onClick={() => { setNewFolderName(''); setNewFolderOpen(true); }}
                  >
                    <FiFolderPlus /> {t('documentLibrary.newFolder', { defaultValue: 'New folder' })}
                  </button>
                )}
                {canUpload && (
                  <>
                    <button
                      type="button"
                      className="doclib-btn doclib-btn--primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? <FiLoader className="doclib-spin" /> : <FiUpload />}
                      {uploading
                        ? t('documentLibrary.uploading', { defaultValue: 'Uploading…' })
                        : t('documentLibrary.uploadFiles', { defaultValue: 'Upload' })}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                      style={{ display: 'none' }}
                      onChange={(e) => handleUploadFiles(Array.from(e.target.files || []))}
                    />
                  </>
                )}
              </div>
            )}

            {!openFolder && (
              <>
                {companiesList.length > 1 && (
                  <select
                    className="doclib-company-filter"
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    aria-label={t('documentLibrary.companyFilter', { defaultValue: 'Company' })}
                  >
                    <option value="">{t('documentLibrary.allCompanies', { defaultValue: 'All companies' })}</option>
                    {companiesList.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                )}
                {/* The company/counterparty levels are system-managed — free
                    folders can only be created at the library root. */}
                {canCreateFolder && !openCompany && (
                  <button
                    type="button"
                    className="doclib-btn"
                    onClick={() => { setNewFolderName(''); setNewFolderOpen(true); }}
                  >
                    <FiFolderPlus /> {t('documentLibrary.newFolder', { defaultValue: 'New folder' })}
                  </button>
                )}
                <div className="doclib-sort" ref={sortRef}>
                  <button
                    type="button"
                    className={`doclib-sort__btn ${sortOpen ? 'doclib-sort__btn--open' : ''}`}
                    onClick={() => setSortOpen(v => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={sortOpen}
                  >
                    <span className="doclib-sort__label">{t('documentLibrary.sortLabel')}</span>
                    <span className="doclib-sort__value">{currentSort.label}</span>
                    <FiChevronDown />
                  </button>
                  {sortOpen && (
                    <div className="doclib-sort__menu" role="listbox">
                      {sortOptions.map(opt => (
                        <button
                          key={opt.id}
                          role="option"
                          aria-selected={sortBy === opt.id}
                          className={`doclib-sort__opt ${sortBy === opt.id ? 'doclib-sort__opt--active' : ''}`}
                          onClick={() => { setSortBy(opt.id); setSortOpen(false); }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="doclib-view" role="tablist" aria-label={t('documentLibrary.sortLabel')}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'grid'}
                    className={`doclib-view__btn ${viewMode === 'grid' ? 'doclib-view__btn--active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    aria-label={t('documentLibrary.viewGrid')}
                    title={t('documentLibrary.viewGrid')}
                  >
                    <FiGrid />
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'list'}
                    className={`doclib-view__btn ${viewMode === 'list' ? 'doclib-view__btn--active' : ''}`}
                    onClick={() => setViewMode('list')}
                    aria-label={t('documentLibrary.viewList')}
                    title={t('documentLibrary.viewList')}
                  >
                    <FiList />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Advanced search: metadata + full text + OCR (server-side,
              permission-scoped). Rendered alongside the client-side tree
              filtering as soon as a real query is typed. */}
          <AdvancedSearchPanel query={search} onOpenResult={openSearchResult} />

          {/* Contents */}
          <div className="doclib-content">
            {!openFolder ? (
              <FolderGrid
                folders={visibleFolders}
                onOpen={openEntry}
                search={search}
                viewMode={viewMode}
                loading={loadingFolders || (!!openCounterparty && bucket.loading && bucket.items.length === 0)}
                footer={openCounterparty && bucket.total > bucket.items.length ? (
                  <div className="doclib-more">
                    <span className="doclib-more__count">
                      {t('documentLibrary.showingOf', {
                        shown: bucket.items.length, total: bucket.total, defaultValue: 'Showing {{shown}} of {{total}}',
                      })}
                    </span>
                    <button type="button" className="doclib-btn" onClick={loadMoreBucket} disabled={bucket.loading}>
                      {bucket.loading ? <FiLoader className="doclib-spin" /> : <FiChevronDown />}
                      {t('documentLibrary.loadMore', { defaultValue: 'Load more' })}
                    </button>
                  </div>
                ) : null}
              />
            ) : (
              <FolderContents
                folder={openFolder}
                childFolders={childFolders}
                folderCounts={folderCounts}
                documents={visibleDocs}
                search={search}
                loading={loadingDocs}
                onOpenSub={openSubFolder}
                onOpenFile={handleOpenFile}
                onOpenAttachment={handleOpenAttachment}
                busyFileId={busyFileId}
              />
            )}
          </div>
        </section>
      </div>

      {/* New-folder modal */}
      {newFolderOpen && (
        <div className="doclib-modal-overlay" onClick={() => !creatingFolder && setNewFolderOpen(false)}>
          <form
            className="doclib-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateFolder}
          >
            <div className="doclib-modal__head">
              <span className="doclib-modal__icon"><FiFolderPlus /></span>
              <h3>{t('documentLibrary.newFolder', { defaultValue: 'New folder' })}</h3>
              <button
                type="button"
                className="doclib-modal__close"
                onClick={() => setNewFolderOpen(false)}
                aria-label={t('documentLibrary.clearSearch')}
              >
                <FiX />
              </button>
            </div>
            <div className="doclib-modal__body">
              <label htmlFor="doclib-folder-name">{t('documentLibrary.folderName', { defaultValue: 'Folder name' })}</label>
              <input
                id="doclib-folder-name"
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={t('documentLibrary.folderNamePlaceholder', { defaultValue: 'e.g. Invoices' })}
                maxLength={120}
              />
            </div>
            <div className="doclib-modal__foot">
              <button type="button" className="doclib-btn" onClick={() => setNewFolderOpen(false)} disabled={creatingFolder}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="doclib-btn doclib-btn--primary" disabled={creatingFolder || !newFolderName.trim()}>
                {creatingFolder
                  ? t('documentLibrary.creating', { defaultValue: 'Creating…' })
                  : t('documentLibrary.create', { defaultValue: 'Create' })}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
